import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { collapseExpand } from '../../animations/animations';
import { DateRangeSelectionEvent, DateRangeWeekDay } from '@rx-applications/shared/utils';

@Component({
  selector: 'rx-datepicker-range',
  templateUrl: './datepicker-range.component.html',
  styleUrls: ['./datepicker-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [collapseExpand],
})
export class RxDatePickerRangeComponent implements OnInit, OnChanges {
  hoveredDate: NgbDate | null;
  startDateNgbDate: NgbDate | null;
  minDateNgbDate: NgbDate | null;
  maxDateNgbDate: NgbDate | null;
  fromDateNgbDate: NgbDate | null;
  toDateNgbDate: NgbDate | null;
  fromModel: Date;
  toModel: Date | null;
  checkinClicked = false;
  checkoutClicked = false;
  navigationNextEnabled = false;
  navigationPreviousEnabled = false;

  /**
   * Set starting date
   */
  @Input() fromDate?: Date;
  /**
   * Set ending date
   */
  @Input() toDate?: Date;
  /**
   * Minimum date
   */
  @Input() minDate?: Date;
  /**
   * Maximum date
   */
  @Input() maxDate?: Date;
  /**
   * (*to review*)
   */
  @Input() startDate?: Date;
  /**
   * Label text for first input
   */
  @Input() labelFrom = 'Check in';
  /**
   * Label text for second input
   */
  @Input() labelTo = 'Check out';
  /**
   * Test id for first input
   */
  @Input() testIdFrom;
  /**
   * Test id for second input
   */
  @Input() testIdTo;
  /**
   * Label text for first input
   */
  @Input() singleInputLabel = 'Dates';
  /**
   * Placeholder text
   */
  @Input() placeholder = 'dd/mm/yy';
  /**
   * Show or hide dates outside current month ("visible", "collapsed", "hidden")
   */
  @Input() outsideDays = 'hidden';
  /**
   * 	Navigation icons ("select", "arrows", "none")
   */
  @Input() navigation: string;
  /**
   * 	Is dropdown open state
   */
  @Input() isOpen = false;

  /**
   * 	If the inputs have to be hidden
   */
  @Input() withoutInputs = false;

  /**
   * 	If single input is shown rather than two inputs
   */
  @Input() singleInput = false;
  /**
   * 	Format of date displayed in Check in/Check out inputs
   */
  @Input() dateFormat = 'EE, d MMM';

  /**
   * Allows to select the same checkin and checkout date
   */
  @Input() allowSameDaySelection = false;

  @Input() showRequierdAsterisk = false;

  @Input() firstDayOfWeek: number = DateRangeWeekDay.Monday;

  /**
   * 	Date range selected
   */
  @Output() dateSelect = new EventEmitter<DateRangeSelectionEvent>();

  static toNgbDate(date: Date): NgbDate | null {
    return date
      ? NgbDate.from({
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear()
      }) : null;
  }

  ngOnInit() {
    this.initialize();
  }

  initialize(): void {
    this.updateDates();
    this.setActiveField();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fromDate'] || changes['toDate']) {
      this.updateDates();
    }
  }

  updateDates(): void {
    this.fromDateNgbDate = RxDatePickerRangeComponent.toNgbDate(this.fromDate);
    this.toDateNgbDate = RxDatePickerRangeComponent.toNgbDate(this.toDate);
    if (this.fromDateNgbDate) {
      this.fromModel = new Date(this.fromDateNgbDate.year, this.fromDateNgbDate.month - 1, this.fromDateNgbDate.day);
    }
    if (this.toDateNgbDate) {
      this.toModel = new Date(this.toDateNgbDate.year, this.toDateNgbDate.month - 1, this.toDateNgbDate.day);
    }
    if (this.startDate) {
      this.startDateNgbDate = RxDatePickerRangeComponent.toNgbDate(this.startDate);
    }
    if (this.minDate) {
      this.minDateNgbDate = RxDatePickerRangeComponent.toNgbDate(this.minDate);
    }
    if (this.maxDate) {
      this.maxDateNgbDate = RxDatePickerRangeComponent.toNgbDate(this.maxDate);
    }
  }

  onDateSelection(date: NgbDate) {
    if (!this.fromDateNgbDate && !this.toDateNgbDate) {
      this.fromDateNgbDate = date;
    } else if (
      this.fromDateNgbDate &&
      !this.toDateNgbDate &&
      ((this.allowSameDaySelection && date.equals(this.fromDateNgbDate)) || date.after(this.fromDateNgbDate))
    ) {
      this.toDateNgbDate = date;
      this.toModel = new Date(this.toDateNgbDate.year, this.toDateNgbDate.month - 1, this.toDateNgbDate.day);
      this.emit(true);
    } else {
      this.toDateNgbDate = null;
      this.toModel = null;
      this.fromDateNgbDate = date;
    }

    this.setActiveField();
    this.fromModel = new Date(this.fromDateNgbDate.year, this.fromDateNgbDate.month - 1, this.fromDateNgbDate.day);
  }

  isHovered(date: NgbDate) {
    return (
      this.fromDateNgbDate &&
      !this.toDateNgbDate &&
      this.hoveredDate &&
      date.after(this.fromDateNgbDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return date.after(this.fromDateNgbDate) && date.before(this.toDateNgbDate);
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.fromDateNgbDate) || date.equals(this.toDateNgbDate) || this.isInside(date) || this.isHovered(date)
    );
  }

  openDatePicker() {
    this.isOpen = true;
    this.startDateNgbDate = RxDatePickerRangeComponent.toNgbDate(this.startDate);
  }

  setActiveField() {
    if (this.fromDateNgbDate && !this.toDateNgbDate) {
      this.checkinClicked = false;
      this.checkoutClicked = true;
    } else {
      this.checkinClicked = true;
      this.checkoutClicked = false;
    }
  }

  public handleClickOutside() {
    if (this.isOpen) {
      this.emit(true);
    }
  }

  private emit(close?: boolean) {
    const dateRange = {
      dateFrom: this.fromModel,
      dateTo: this.toModel,
    };

    this.dateSelect.emit({ ...{}, ...dateRange });
    if (close) {
      this.isOpen = false;
    }
  }

  onDateNavigation(date: any) {
    if (date.next) {
      this.navigationPreviousEnabled = true;
      this.navigationNextEnabled = true;
      const nextDate = { month: date.next.month + 1, year: date.next.year };
      const prevDate = { month: date.next.month, year: date.next.year };

      if (this.maxDateNgbDate && nextDate.year === this.maxDateNgbDate.year && nextDate.month >= this.maxDateNgbDate.month) {
        this.navigationNextEnabled = false;
      }

      if (this.minDateNgbDate && prevDate.year === this.minDateNgbDate.year && prevDate.month <= this.minDateNgbDate.month) {
        this.navigationPreviousEnabled = false;
      }
    }
  }
}
